import { EXPORT_COLORS } from '@consigli/types';
import { saveBlob } from '@consigli/utils';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  TableLayoutType,
  BorderStyle,
  ShadingType,
  AlignmentType,
  VerticalAlign,
  ImageRun,
  Header,
} from 'docx';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { toUTC } from '../format-meeting-datetime';
import {
  toTitleCase,
  stripDateFromTitle,
  getExportDateSuffix,
  sanitizeFilename,
} from '../utils/export-types';
import { fetchLogoAsArrayBuffer } from '../utils/logo-utils';
import { formatResponsibleParties } from '../utils/participant-utils';
import {
  parseSummaryIntoParts,
  stripSpeakerPrefix,
  extractDiscussionStructure,
  extractSummaryIntro,
} from '../utils/text-utils';

import type { ExportData } from '../utils/export-types';

export type ExportDocxData = ExportData;

// Helper to create a header cell with light grey background and black text
const createHeaderCell = (text: string, width?: number, centered = false) => {
  return new TableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text, bold: true, color: EXPORT_COLORS.black.hex, size: 24 })],
        alignment: centered ? AlignmentType.CENTER : AlignmentType.LEFT,
      }),
    ],
    shading: { fill: EXPORT_COLORS.headerGrey.hex, type: ShadingType.CLEAR, color: 'auto' },
    verticalAlign: VerticalAlign.CENTER,
    width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
  });
};

// Helper to create a data cell
const createDataCell = (text: string, centered = false) => {
  return new TableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text, size: 24 })],
        alignment: centered ? AlignmentType.CENTER : AlignmentType.LEFT,
        spacing: { line: 200 },
      }),
    ],
    verticalAlign: VerticalAlign.CENTER,
    margins: { top: 100, bottom: 100, left: 100, right: 100 },
  });
};

export const useExportDocx = () => {
  const { t, i18n } = useTranslation();
  const exportToDocx = useCallback(
    async (data: ExportDocxData) => {
      const { meeting, transcripts, summary, actionItems, agreements } = data;

      const startDate = new Date(toUTC(meeting.scheduledStartTime));
      const endDate = new Date(toUTC(meeting.scheduledEndTime));
      const duration = Math.round((endDate.getTime() - startDate.getTime()) / 60000);

      const dateStr = startDate.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const timeStr = startDate.toLocaleTimeString(i18n.language, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      const children: (Paragraph | Table)[] = [];

      // Title - Meeting name (black, bold) - strip date if present since it's shown below
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: toTitleCase(stripDateFromTitle(meeting.title)),
              bold: true,
              size: 32, // 16pt
              color: EXPORT_COLORS.black.hex,
            }),
          ],
          spacing: { after: 100 },
        }),
      );

      // Meeting metadata line (grey)
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${t('meeting-secretary.exports.headers.date')}: ${dateStr}  |  ${t('meeting-secretary.exports.headers.time')}: ${timeStr} (${duration} min)  |  ${t('meeting-secretary.exports.headers.organizer')}: ${meeting.organizerEmail ?? ''}`,
              size: 18,
              color: '646464', // Grey text
            }),
          ],
          spacing: { after: 300 },
        }),
      );

      const addSectionHeader = (title: string) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: title,
                bold: true,
                size: 28, // 14pt
                color: EXPORT_COLORS.black.hex,
              }),
            ],
            spacing: { before: 400, after: 200 },
            border: {
              bottom: {
                color: 'auto',
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
          }),
        );
      };

      // Summary Section (keep as paragraphs - it's prose text)
      if (summary) {
        addSectionHeader(t('meeting-secretary.exports.summary'));

        const discussionStructure = extractDiscussionStructure(summary.generatedSummaryText);

        if (discussionStructure.length > 0) {
          // Structured summary: intro prose followed by the "Key Discussion
          // Points" breakdown. The intro excludes the topic bullets so they are
          // not duplicated by the structured list below.
          const intro = extractSummaryIntro(summary.generatedSummaryText);
          if (intro) {
            children.push(
              new Paragraph({
                text: intro,
                spacing: { after: 200 },
              }),
            );
          }

          // Add Key Discussion Points header
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: t('meeting-secretary.exports.key-discussion-points'),
                  bold: true,
                  size: 28,
                  color: EXPORT_COLORS.black.hex,
                }),
              ],
              spacing: { before: 200, after: 100 },
            }),
          );

          // Add each section with bold header and indented bullets
          discussionStructure.forEach((section) => {
            // Add section header as bold
            children.push(
              new Paragraph({
                children: [new TextRun({ text: section.header, bold: true })],
                spacing: { before: 100, after: 50 },
              }),
            );

            // Add section's bullet points with indentation
            section.points.forEach((point) => {
              children.push(
                new Paragraph({
                  children: [new TextRun({ text: point })],
                  bullet: { level: 1 }, // Indented bullet
                  spacing: { after: 50 },
                }),
              );
            });
          });
        } else {
          // Unstructured summary: prose plus a flat bullet list (legacy layout).
          const { mainText, bulletPoints } = parseSummaryIntoParts(summary.generatedSummaryText);

          if (mainText) {
            children.push(
              new Paragraph({
                text: mainText,
                spacing: { after: 200 },
              }),
            );
          }

          if (bulletPoints.length > 0) {
            bulletPoints.forEach((point: string) => {
              children.push(
                new Paragraph({
                  children: [new TextRun({ text: point })],
                  bullet: { level: 0 },
                  spacing: { after: 100 },
                }),
              );
            });
          }
        }
      }

      // Action Items Section - TABLE format: ID | Action | Responsible
      if (actionItems.length > 0) {
        addSectionHeader(t('meeting-secretary.exports.action-items'));

        const actionItemRows = [
          // Header row
          new TableRow({
            children: [
              createHeaderCell(t('meeting-secretary.exports.headers.id'), 5, true),
              createHeaderCell(t('meeting-secretary.exports.headers.action'), 48),
              createHeaderCell(t('meeting-secretary.exports.headers.responsible'), 47),
            ],
          }),
          // Data rows
          ...actionItems.map(
            (item, index) =>
              new TableRow({
                children: [
                  createDataCell(String(index + 1), true),
                  createDataCell(item.descriptionText),
                  createDataCell(item.responsiblePerson?.name || ''),
                ],
              }),
          ),
        ];

        children.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            layout: TableLayoutType.FIXED,
            rows: actionItemRows,
          }),
        );
      }

      // Agreements Section - TABLE format: ID | Agreement | Responsible | Details
      if (agreements.length > 0) {
        addSectionHeader(t('meeting-secretary.exports.agreements'));

        const agreementRows = [
          // Header row
          new TableRow({
            children: [
              createHeaderCell(t('meeting-secretary.exports.headers.id'), 5, true),
              createHeaderCell(t('meeting-secretary.exports.headers.agreement'), 33),
              createHeaderCell(t('meeting-secretary.exports.headers.responsible'), 31),
              createHeaderCell(t('meeting-secretary.exports.headers.details'), 31),
            ],
          }),
          // Data rows
          ...agreements.map(
            (agreement, index) =>
              new TableRow({
                children: [
                  createDataCell(String(index + 1), true),
                  createDataCell(agreement.agreement),
                  createDataCell(formatResponsibleParties(agreement.responsibleParties)),
                  createDataCell(
                    stripSpeakerPrefix(
                      agreement.sourceTextFromTranscript,
                      formatResponsibleParties(agreement.responsibleParties),
                    ),
                  ),
                ],
              }),
          ),
        ];

        children.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            layout: TableLayoutType.FIXED,
            rows: agreementRows,
          }),
        );
      }

      // Transcript Section - TABLE format: ID | Timestamp | Speaker | Text
      if (transcripts.length > 0) {
        addSectionHeader(t('meeting-secretary.exports.transcript'));

        const transcriptRows = [
          // Header row
          new TableRow({
            children: [
              createHeaderCell(t('meeting-secretary.exports.headers.id'), 5, true),
              createHeaderCell(t('meeting-secretary.exports.headers.timestamp'), 15),
              createHeaderCell(t('meeting-secretary.exports.headers.speaker'), 16),
              createHeaderCell(t('meeting-secretary.exports.headers.text'), 64),
            ],
          }),
          // Data rows
          ...transcripts.map(
            (entry, index) =>
              new TableRow({
                children: [
                  createDataCell(String(index + 1), true),
                  createDataCell(entry.startTime),
                  createDataCell(entry.speaker),
                  createDataCell(entry.text),
                ],
              }),
          ),
        ];

        children.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            layout: TableLayoutType.FIXED,
            rows: transcriptRows,
          }),
        );
      }

      // Fetch the logo image
      const logoBuffer = await fetchLogoAsArrayBuffer();

      const doc = new Document({
        sections: [
          {
            properties: {},
            headers: {
              default: new Header({
                children: [
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                      new ImageRun({
                        data: logoBuffer,
                        transformation: {
                          width: 100,
                          height: 38,
                        },
                        type: 'png',
                      }),
                    ],
                  }),
                ],
              }),
            },
            children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const titleCaseFilename = toTitleCase(stripDateFromTitle(meeting.title));
      const sanitizedFilename = sanitizeFilename(titleCaseFilename);
      const dateSuffix = getExportDateSuffix(meeting.title, meeting.scheduledStartTime);
      const filename = `${sanitizedFilename}${dateSuffix}.docx`;
      saveBlob(filename, blob);
    },
    [t, i18n.language],
  );

  return { exportToDocx };
};
